import { Link } from "react-router-dom";

const SANDBOX = "[https://sandbox.lumorasites.com](https://sandbox.lumorasites.com)";
const DEV_API_TOS =
  "[https://github.com/bahaaaldin214/lumora/blob/main/docs/business/developer-api-tos.md](https://github.com/bahaaaldin214/lumora/blob/main/docs/business/developer-api-tos.md)";

/**
 * Human-first API docs landing (#252). Full schemas live in Redoc / OpenAPI.
 * Documents soft launch vs public Full API tiers, web-only slug claim (#247),
 * email verification (#246), and paid/approved publish gate (#248).
 */
export function DevelopersPage({ className }: { className?: string }) {
  return (
    <article className={className} data-lumora-view="developers">
      <p className="site-signin-kicker">Developers</p>
      <h1>Build on Lumora</h1>
      <p className="portal-lead">
        Use our shared backend from your own React (or any) frontend. You write the UI; we
        host auth, data, and checkout. No backend required on your side.
      </p>

      <section className="portal-section">
        <h2>Soft launch vs Full API</h2>
        <p>
          Docs and sandbox are in a <strong>soft launch</strong>. Sandbox (<code>dev-sandbox</code>)
          is $0 after email verification, with a <strong>planned cap</strong> of 8,000 API requests/month (not enforced yet),
          and requires <strong>Lumora operator approval</strong> before a publication goes live.
          Paid Full API plans (<strong>Starter $19.99/mo</strong>, <strong>Growth $79/mo</strong>,{" "}
          <strong>Pro $299/mo</strong>) unlock <strong>auto-publish</strong>.
          Site slugs can only be claimed on the Lumora website with a verified email — never via open API minting.
        </p>
      </section>

      <section className="portal-section">
        <h2>Sandbox</h2>
        <p>
          Base URL:{" "}
          <code className="developers-code">{SANDBOX}</code>
        </p>
        <p>
          Sandbox site slug: <code className="developers-code">dev-sandbox</code> — verify your
          email on the website first. Lumora assigns or web-claims the slug for any new site.
        </p>
        <p className="muted">
          Quotas (provisional): abuse paths ~30 req/IP/min; uploads ~10&nbsp;MiB (100 mints/mo cap,
          metered on confirmed PUT); planned cap 8,000 API requests/month on sandbox (not enforced yet).
        </p>
        <pre className="developers-prompt">{`curl ${SANDBOX}/health`}</pre>
      </section>

      <section className="portal-section">
        <h2>Wire your app</h2>
        <p>In Vite, add to <code className="developers-code">.env.local</code>:</p>
        <pre className="developers-prompt">{`VITE_LUMORA_API=${SANDBOX}`}</pre>
        <p>
          Put all API calls in one module (e.g. <code className="developers-code">src/api/lumora.ts</code>)
          so swapping the base URL later is easy.
        </p>
      </section>

      <section className="portal-section">
        <h2>Auth, verification &amp; publish</h2>
        <ol className="developers-list">
          <li>
            Verify your signup email via{" "}
            <code className="developers-code">POST /v1/auth/verify-email/start</code> and{" "}
            <code className="developers-code">POST /v1/auth/verify-email/confirm</code>.
          </li>
          <li>
            <code className="developers-code">POST /v1/auth/login</code> with{" "}
            <code className="developers-code">{`{ site, email, password }`}</code>.
          </li>
          <li>
            Mint a scoped machine key with <code className="developers-code">POST /v1/developer/keys</code>{" "}
            or use your access JWT to submit a build to{" "}
            <code className="developers-code">POST /v1/sites/{"{slug}"}/publish</code>.
          </li>
        </ol>
      </section>

      <section className="portal-section">
        <h2>Useful routes</h2>
        <p className="muted">Public (no token) — use the site slug Lumora assigned:</p>
        <ul className="developers-list">
          <li><code className="developers-code">GET /v1/sites/{"{slug}"}</code></li>
          <li><code className="developers-code">GET /v1/sites/{"{slug}"}/menu</code></li>
          <li><code className="developers-code">GET /v1/sites/{"{slug}"}/content</code></li>
          <li><code className="developers-code">GET /v1/sites/{"{slug}"}/plans</code></li>
          <li><code className="developers-code">GET /v1/slugs/{"{slug}"}/availability</code></li>
        </ul>
        <p className="muted">Signed in (verified email):</p>
        <ul className="developers-list">
          <li><code className="developers-code">PUT /v1/sites/{"{slug}"}/content</code> (your tenant only)</li>
          <li><code className="developers-code">POST /v1/sites/{"{slug}"}/publish</code> (paid auto-publish or sandbox approval queue)</li>
          <li><code className="developers-code">GET /v1/sites/{"{slug}"}/publish/status</code></li>
          <li><code className="developers-code">GET /v1/orders</code></li>
        </ul>
      </section>

      <section className="portal-section">
        <h2>Full reference</h2>
        <p>
          <a className="developers-cta" href="./api-docs.html">
            Open API reference
          </a>{" "}
          (every external path + schemas) ·{" "}
          <a href="./openapi.external.yaml">openapi.external.yaml</a>
        </p>
        <p>
          <a href={DEV_API_TOS}>Developer API terms</a> (draft — attorney review pending before
          public sign-off).
        </p>
      </section>

      <section className="portal-section developers-ai-optional">
        <h2>Optional: AI coding assistants</h2>
        <p className="muted">
          Paste this handoff prompt into your AI coding assistant:
        </p>
        <pre className="developers-prompt">{`React frontend for the Lumora site with slug "dev-sandbox".
Base URL: ${SANDBOX} (or VITE_LUMORA_API).
Use the Lumora API guide (docs/api/guide.md) and openapi.external.yaml as the contract. Do not call routes that are not listed there.
Never call /v1/admin/* or /v1/edge/*. Do not attempt to mint slugs via API (web-only claim with verified email).
Publish via POST /v1/sites/{slug}/publish only after email verification and paid subscription or Lumora operator approval.
Keep all fetch calls in one adapter module.`}</pre>
      </section>

      <p className="muted">
        Need an account? <Link to="/contact">Contact Lumora</Link> ·{" "}
        <Link to="/signin">Owner sign in</Link>
      </p>
    </article>
  );
}
